import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = ({ isModal = false }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);
  const isMobile = window.innerWidth < 768;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            {!isModal && !isMobile && (
              <Button
                onClick={() => window.history.back()}
                variant="outline"
                className="mb-6 group hover:bg-primary/10"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:transform group-hover:-translate-x-1 transition-transform" />
                Back
              </Button>
            )}
            <h1 className="text-4xl md:text-5xl font-bold gradient-text-brand mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-muted-foreground">
              <strong>Last Updated:</strong> November, 2025
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-card/50 backdrop-blur-sm rounded-2xl p-8 border border-border/50 space-y-8">
              
              {/* Introduction */}
              <div className="space-y-4">
                <p className="text-foreground/90 leading-relaxed">
                  Welcome to VREELS! These Terms of Service ("Terms") govern your use of our website, mobile application, and related services (collectively, the "Services") operated by Maha Naga Kali LLC, doing business as VREELS ("we", "us", or "our"). By accessing or using our Services, you agree to be bound by these Terms.
                </p>
              </div>

              {/* Section 1 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">1. Acceptance of Terms</h2>
                <p className="text-foreground/90">
                  By accessing and using VREELS, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </div>

              {/* Section 2 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">2. User Accounts</h2>
                <p className="text-foreground/90">
                  When you create an account with us, you must provide information that is accurate, complete, and current at all times. You are responsible for safeguarding the password and for all activities that occur under your account.
                </p>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>You must be at least 13 years old to use our Services</li>
                  <li>You are responsible for maintaining the security of your account</li>
                  <li>You must not share your account credentials with others</li>
                  <li>You must notify us immediately of any unauthorized use of your account</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">3. Username and Account Name Restrictions</h2>
                <p className="text-foreground/90">
                  To maintain a safe and respectful environment, we have established specific restrictions for usernames and account names:
                </p>
                
                <h3 className="text-xl font-semibold text-brand-orange mt-6">Prohibited Content in Usernames</h3>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>You may not register or use a username that contains offensive, obscene, defamatory, harassing, hateful, sexually explicit, discriminatory, or otherwise inappropriate language or content. Any username that promotes violence, illegal activity, or hate speech is strictly prohibited.</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-brand-orange mt-6">Impersonation and Misrepresentation</h3>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>Usernames that impersonate or appear to represent another person, entity, organization, or brand, including but not limited to "admin," "moderator," "official," or the names of well-known companies or public figures, are not permitted. Usernames may not mislead other users into believing there is an affiliation, sponsorship, or endorsement where none exists.</li>
                </ul>
                  
                <h3 className="text-xl font-semibold text-brand-orange mt-6">Reserved and Restricted Terms</h3>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>Certain usernames are reserved for system functions, security purposes, or internal use (such as "system," "root," "support," or variations thereof) and may not be registered or used by users under any circumstances.</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-brand-orange mt-6">Enforcement</h3>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>We reserve the right, in our sole discretion, to suspend, restrict, or permanently delete any account found to be in violation of these username restrictions, without prior notice. Continued or severe violations may result in a permanent ban from the platform.</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">4. User Content</h2>
                <p className="text-foreground/90">
                  Our Services allow you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ("Content"). You are responsible for the Content that you post to the Services.
                </p>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>You retain ownership of your Content</li>
                  <li>You grant us a license to use, modify, publicly perform, publicly display, reproduce, and distribute your Content</li>
                  <li>You must not post Content that is illegal, harmful, threatening, abusive, or violates others' rights</li>
                  <li>We reserve the right to remove Content that violates these Terms</li>
                </ul>
              </div>

              {/* Section 5 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">5. Prohibited Uses</h2>
                <p className="text-foreground/90">
                  You may not use our Services:
                </p>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                  <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                  <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                  <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                  <li>To submit false or misleading information</li>
                </ul>
              </div>

              {/* Section 6 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">6. Intellectual Property Rights</h2>
                <p className="text-foreground/90">
                  The Services and their original content, features, and functionality are and will remain the exclusive property of VREELS and its licensors. The Services are protected by copyright, trademark, and other laws.
                </p>
              </div>

              {/* Section 7 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">7. Termination</h2>
                <p className="text-foreground/90">
                  We may terminate or suspend your account and bar access to the Services immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
                </p>
              </div>

              {/* Section 8 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">8. Disclaimer</h2>
                <p className="text-foreground/90">
                  The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, this Company excludes all representations, warranties, conditions and terms whether express or implied, statutory or otherwise.
                </p>
              </div>

              {/* Section 9 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">9. Limitation of Liability</h2>
                <p className="text-foreground/90">
                  In no event shall VREELS, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Services.
                </p>
              </div>

              {/* Section 10 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">10. Changes to Terms</h2>
                <p className="text-foreground/90">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
                </p>
              </div>

              {/* Section 11 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">11. Contact Information</h2>
                <p className="text-foreground/90">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <div className="bg-card/30 p-4 rounded-lg border border-border/30">
                  <p className="text-foreground/90">
                    Maha Naga Kali LLC, DBA Vreels<br />
                    204 W J Boaz Rd, Saginaw, TX 76179<br />
                    <a href="mailto:mnkllc@vreels.com" className="text-brand-orange hover:text-grad-orange-1 transition-colors">
                      support@vreels.com
                    </a>
                  </p>
                </div>
              </div>

              {/* Back to Top Button */}
              <div className="text-center pt-8">
                <Button
                  onClick={scrollToTop}
                  className="btn-gradient-orange"
                >
                  Back to Top
                </Button>
              </div>

            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 
